import { useState, useRef, useCallback, useEffect } from 'react';
import { ClipboardList, ChevronDown, AlertCircle, Clock, Route, Zap, Bot, BookOpen, GitBranch, Database } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { WARNING } from '@/styles/tokens';
import { usePlanStore } from '@/stores/ai/plan-store';
import type { PlanConversationMessage } from '@/stores/ai/plan-store';
import type { PlanPipelineMode } from '@/types/plan';
import { config as appConfig } from '@/config';

const PLAN_TEMPLATES: Array<{
  label: string;
  description: string;
  turns: PlanConversationMessage[];
}> = [
  {
    label: 'IoT Dashboard',
    description: 'Sensor data visualization with alerts',
    turns: [
      { role: 'user', content: 'I want to build an IoT dashboard that shows real-time sensor data with threshold-based alerts.' },
      { role: 'assistant', content: 'Great! Let me help you plan an IoT dashboard. What types of sensors will you be monitoring (e.g., temperature, humidity, soil moisture)? And what alert thresholds matter most?' },
    ],
  },
  {
    label: 'Data Entry Form',
    description: 'Multi-step form with validation',
    turns: [
      { role: 'user', content: 'I need a multi-step data entry form with field validation and progress tracking.' },
      { role: 'assistant', content: 'I can help plan that! What data are you collecting? How many steps should the form have, and what validation rules do you need?' },
    ],
  },
  {
    label: 'Monitoring Display',
    description: 'Real-time status board',
    turns: [
      { role: 'user', content: 'Build a monitoring display that shows real-time system status with visual indicators.' },
      { role: 'assistant', content: 'Let\'s plan a monitoring display. What systems or metrics do you want to track? Should it include historical trends or just live status?' },
    ],
  },
  {
    label: 'Report Builder',
    description: 'Data query + chart layout',
    turns: [
      { role: 'user', content: 'I want a report builder page with data queries and configurable chart layouts.' },
      { role: 'assistant', content: 'A report builder — nice! What data sources will the reports pull from? Do you need multiple chart types (line, bar, pie) or a specific set?' },
    ],
  },
];

interface PlanModeToggleProps {
  onOpenRoadmapModal?: () => void;
}

export function PlanModeToggle({ onOpenRoadmapModal }: PlanModeToggleProps = {}) {
  const isActive = usePlanStore((s) => s.isActive);
  const pipelineMode = usePlanStore((s) => s.pipelineMode);
  const planHistory = usePlanStore((s) => s.planHistory);
  const toggle = usePlanStore((s) => s.toggle);
  const setPipelineMode = usePlanStore((s) => s.setPipelineMode);
  const activate = usePlanStore((s) => s.activate);
  const setConversationHistory = usePlanStore((s) => s.setConversationHistory);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [knowledgeStatus, setKnowledgeStatus] = useState<'unknown' | 'available' | 'unavailable'>('unknown');
  const knowledgeCheckRef = useRef<number>(0);

  const checkKnowledgeService = useCallback(() => {
    const now = Date.now();
    if (now - knowledgeCheckRef.current < 5000) return;
    knowledgeCheckRef.current = now;

    fetch(`${appConfig.api.knowledgeServiceUrl}/api/v1/health`, { signal: AbortSignal.timeout(3000) })
      .then((res) => setKnowledgeStatus(res.ok ? 'available' : 'unavailable'))
      .catch(() => setKnowledgeStatus('unavailable'));
  }, []);

  const handleTemplateClick = useCallback(
    (turns: PlanConversationMessage[]) => {
      if (!isActive) activate();
      setConversationHistory(turns);
    },
    [isActive, activate, setConversationHistory],
  );

  const handleSkillWizardClick = useCallback(() => {
    if (!isActive) activate();
    setPipelineMode('skill');
    setConversationHistory([
      { role: 'user', content: 'I want to create a new agent skill.' },
      { role: 'assistant', content: "Let's build a skill! What should this skill do? Describe the task or workflow you want the agent to handle." },
    ]);
    setPopoverOpen(false);
  }, [isActive, activate, setPipelineMode, setConversationHistory]);

  const handleAgentWizardClick = useCallback(() => {
    if (!isActive) activate();
    setPipelineMode('agent');
    setConversationHistory([
      { role: 'user', content: 'I want to create a new agent.' },
      { role: 'assistant', content: "Let's build an agent! What should this agent do? Describe its purpose and what kind of tasks it should handle." },
    ]);
    setPopoverOpen(false);
  }, [isActive, activate, setPipelineMode, setConversationHistory]);

  const handleKnowledgeWizardClick = useCallback(() => {
    if (!isActive) activate();
    setPipelineMode('knowledge');
    setConversationHistory([
      { role: 'user', content: 'I want to create a new knowledge base.' },
      { role: 'assistant', content: "Let's set up a knowledge base! What domain or topic should it cover? And what kind of information will it contain?" },
    ]);
    setPopoverOpen(false);
  }, [isActive, activate, setPipelineMode, setConversationHistory]);

  const handleWorkflowWizardClick = useCallback(() => {
    if (!isActive) activate();
    setPipelineMode('workflow');
    setConversationHistory([
      { role: 'user', content: 'I want to create a new workflow.' },
      { role: 'assistant', content: "Let's design a workflow! What should trigger it, and what actions should it perform?" },
    ]);
    setPopoverOpen(false);
  }, [isActive, activate, setPipelineMode, setConversationHistory]);

  const handleDataWizardClick = useCallback(() => {
    if (!isActive) activate();
    setPipelineMode('data');
    setConversationHistory([
      { role: 'user', content: 'I want to create a new data schema.' },
      { role: 'assistant', content: "Let's define a data schema! What kind of data will you be storing? Describe the fields and their types." },
    ]);
    setPopoverOpen(false);
  }, [isActive, activate, setPipelineMode, setConversationHistory]);

  const recentPlans = planHistory.slice(0, 5);

  return (
    <TooltipProvider>
      <div className="flex items-center">
        {/* Main pill toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-pressed={isActive}
              onClick={() => toggle()}
              className="flex items-center gap-1.5 h-8 rounded-l-full px-3"
              style={{
                border: `1px solid ${isActive ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                borderRight: 'none',
                background: isActive ? 'hsl(var(--primary))' : 'transparent',
                color: isActive ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                transition: 'all 0.15s ease',
              }}
            >
              <ClipboardList style={{ width: 16, height: 16 }} />
              Plan
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Plan Mode (Shift+Tab) — Define what to build before executing</p>
          </TooltipContent>
        </Tooltip>

        {/* Dropdown trigger */}
        <Popover open={popoverOpen} onOpenChange={(open) => { setPopoverOpen(open); if (open) checkKnowledgeService(); }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-expanded={popoverOpen}
              className="flex items-center h-8 rounded-r-full px-1.5"
              style={{
                border: `1px solid ${isActive ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                borderLeft: `1px solid ${isActive ? 'hsl(var(--primary-foreground) / 0.19)' : 'hsl(var(--border))'}`,
                background: isActive ? 'hsl(var(--primary))' : 'transparent',
                color: isActive ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <ChevronDown style={{ width: 14, height: 14 }} />
            </button>
          </PopoverTrigger>

          <PopoverContent align="start" sideOffset={4} style={{ width: 260, padding: 0 }}>
            {/* Mode selector (hidden during wizard modes) */}
            {(['skill', 'agent', 'knowledge', 'workflow', 'data'] as PlanPipelineMode[]).includes(pipelineMode) ? (
              <div style={{ padding: '12px 14px', borderBottom: '1px solid hsl(var(--border))', display: 'flex', alignItems: 'center', gap: 6 }}>
                {pipelineMode === 'skill' && <Zap style={{ width: 14, height: 14, color: 'hsl(var(--primary))' }} />}
                {pipelineMode === 'agent' && <Bot style={{ width: 14, height: 14, color: 'hsl(var(--primary))' }} />}
                {pipelineMode === 'knowledge' && <BookOpen style={{ width: 14, height: 14, color: 'hsl(var(--primary))' }} />}
                {pipelineMode === 'workflow' && <GitBranch style={{ width: 14, height: 14, color: 'hsl(var(--primary))' }} />}
                {pipelineMode === 'data' && <Database style={{ width: 14, height: 14, color: 'hsl(var(--primary))' }} />}
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {pipelineMode === 'skill' ? 'Skill' : pipelineMode === 'agent' ? 'Agent' : pipelineMode === 'knowledge' ? 'Knowledge' : pipelineMode === 'workflow' ? 'Workflow' : 'Data'} Wizard Active
                </span>
              </div>
            ) : (
            <div style={{ padding: '12px 14px', borderBottom: '1px solid hsl(var(--border))' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', marginBottom: 8 }}>
                Mode
              </div>
              {(['full', 'quick'] as PlanPipelineMode[]).map((mode) => (
                <label
                  key={mode}
                  className="flex items-center gap-2"
                  style={{ cursor: 'pointer', padding: '4px 0', fontSize: 13 }}
                >
                  <input
                    type="radio"
                    name="plan-mode"
                    role="radio"
                    aria-checked={pipelineMode === mode}
                    checked={pipelineMode === mode}
                    onChange={() => setPipelineMode(mode)}
                    style={{ accentColor: 'hsl(var(--primary))' }}
                  />
                  <span style={{ fontWeight: pipelineMode === mode ? 600 : 400 }}>
                    {mode === 'full' ? 'Full Mode' : 'Quick Mode'}
                    {mode === 'full' && (
                      <span style={{ color: 'hsl(var(--muted-foreground) / 0.6)', fontSize: 11, marginLeft: 4 }}>(default)</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            )}

            {/* View Roadmaps */}
            {onOpenRoadmapModal && (
              <div style={{ padding: '8px 14px', borderBottom: '1px solid hsl(var(--border))' }}>
                <button
                  type="button"
                  onClick={() => { setPopoverOpen(false); onOpenRoadmapModal(); }}
                  className="flex items-center gap-2"
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: 4,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'hsl(var(--primary))',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--primary) / 0.03)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <Route style={{ width: 14, height: 14 }} />
                  View Roadmaps
                </button>
              </div>
            )}

            {/* Templates */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid hsl(var(--border))' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', marginBottom: 8 }}>
                Templates
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {PLAN_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.label}
                    type="button"
                    onClick={() => handleTemplateClick(tpl.turns)}
                    style={{
                      display: 'block',
                      textAlign: 'left',
                      padding: '6px 8px',
                      borderRadius: 4,
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      width: '100%',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--primary) / 0.024)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                  >
                    <div style={{ fontWeight: 500 }}>{tpl.label}</div>
                    <div style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginTop: 1 }}>{tpl.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Wizards */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid hsl(var(--border))' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', marginBottom: 8 }}>
                Wizards
              </div>
              {[
                { handler: handleSkillWizardClick, icon: <Zap style={{ width: 14, height: 14, color: 'hsl(var(--primary))', flexShrink: 0 }} />, label: 'Create Skill', desc: 'Guided agent skill builder' },
                { handler: handleAgentWizardClick, icon: <Bot style={{ width: 14, height: 14, color: 'hsl(var(--primary))', flexShrink: 0 }} />, label: 'Create Agent', desc: 'Build a custom agent' },
                { handler: handleKnowledgeWizardClick, icon: <BookOpen style={{ width: 14, height: 14, color: 'hsl(var(--primary))', flexShrink: 0 }} />, label: 'Create Knowledge Base', desc: 'Set up a domain knowledge store' },
                { handler: handleWorkflowWizardClick, icon: <GitBranch style={{ width: 14, height: 14, color: 'hsl(var(--primary))', flexShrink: 0 }} />, label: 'Create Workflow', desc: 'Design an automated workflow' },
                { handler: handleDataWizardClick, icon: <Database style={{ width: 14, height: 14, color: 'hsl(var(--primary))', flexShrink: 0 }} />, label: 'Create Data Schema', desc: 'Define a data model' },
              ].map((wiz) => (
                <button
                  key={wiz.label}
                  type="button"
                  onClick={wiz.handler}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    textAlign: 'left',
                    padding: '6px 8px',
                    borderRadius: 4,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    width: '100%',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--primary) / 0.024)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  {wiz.icon}
                  <div>
                    <div style={{ fontWeight: 500 }}>{wiz.label}</div>
                    <div style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginTop: 1 }}>{wiz.desc}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Recent Plans */}
            {recentPlans.length > 0 && (
              <div style={{ padding: '12px 14px', borderBottom: '1px solid hsl(var(--border))' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', marginBottom: 8 }}>
                  Recent Plans
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {recentPlans.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => {
                        if (!isActive) activate();
                        // Restore is handled via Phase 6 orchestration — for now, surface the plan
                      }}
                      className="flex items-center gap-2"
                      style={{ fontSize: 12, padding: '4px 0', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                    >
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(var(--muted-foreground) / 0.6)' }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {plan.title}
                      </span>
                      {plan.auditScore != null && (
                        <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--primary))' }}>
                          {plan.auditScore}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Knowledge Service status */}
            {knowledgeStatus === 'unavailable' && (
              <div
                className="flex items-center gap-1.5"
                style={{
                  padding: '8px 14px',
                  fontSize: 11,
                  color: WARNING.DEFAULT,
                }}
              >
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                Research unavailable — planning works without web research.
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  );
}
